import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { ProjectDetail, ProjectFeature, ProjectSectionAsset, ProjectStory } from '../../types/content';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import { useAuth } from '../../context/AuthContext';
import { COURSE_IMAGES } from '../../config/courseImages';
import HeroBackdrop from '../../components/UI/HeroBackdrop';
import { heroImageFade, heroShell } from '../../content/heroThemes';
import { formatApproxHours, projectAudienceLabel, splitTechStack } from '../../utils/projectDisplay';
import DetailAccordion from '../../components/content/DetailAccordion';
import ContentBlocks from '../../components/content/ContentBlocks';

type SectionId = 'tech' | 'overview' | 'spec' | 'requirements' | 'wireframes' | 'architecture' | 'errors';

const SECTIONS: { id: SectionId; label: string }[] = [
  { id: 'tech', label: 'Tech stack' },
  { id: 'overview', label: 'Overview' },
  { id: 'spec', label: 'Spec' },
  { id: 'requirements', label: 'Requirements' },
  { id: 'wireframes', label: 'Wireframes' },
  { id: 'architecture', label: 'Architecture' },
  { id: 'errors', label: 'Error messages' },
];

const isImageAsset = (asset: ProjectSectionAsset) =>
  (asset.media?.contentType || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(asset.media?.url || asset.media?.fileName || '');

const AssetList: React.FC<{ assets: ProjectSectionAsset[] }> = ({ assets }) => {
  if (assets.length === 0) return null;
  return (
    <div className="mt-6 grid sm:grid-cols-2 gap-4">
      {assets.map((asset) => (
        <Card key={asset.id || asset.title} className="p-4">
          {asset.media?.url && isImageAsset(asset) && (
            <img src={asset.media.url} alt={asset.title} className="mb-3 w-full rounded-lg object-contain bg-gray-50 max-h-48" />
          )}
          {asset.media?.url && !isImageAsset(asset) && (
            <a href={asset.media.url} target="_blank" rel="noreferrer" className="text-sm text-primary-700">
              {asset.media.fileName || asset.title || 'Open file'}
            </a>
          )}
          <p className="font-medium text-gray-900">{asset.title}</p>
        </Card>
      ))}
    </div>
  );
};

const ProjectDetailPage: React.FC = () => {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [gig, setGig] = useState<ProjectDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [gitRepoUrl, setGitRepoUrl] = useState('');
  const [architectureDocUrl, setArchitectureDocUrl] = useState('');
  const [setupInstructionsUrl, setSetupInstructionsUrl] = useState('');
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [section, setSection] = useState<SectionId>('tech');
  const [openPanel, setOpenPanel] = useState<string>('tech');
  const [openEpicKey, setOpenEpicKey] = useState<string | null>(null);
  const [openFeatureKey, setOpenFeatureKey] = useState<string | null>(null);

  const reload = () => {
    if (!slug) return;
    contentApi.getProjectBySlug(slug).then((r) => {
      const project = r.data.project;
      setGig(project);
      setGitRepoUrl(project.enrollment?.gitRepoUrl || '');
      setArchitectureDocUrl(project.enrollment?.architectureDocUrl || '');
      setSetupInstructionsUrl(project.enrollment?.setupInstructionsUrl || '');
    }).catch(() => setError('This project is not available.'));
  };

  useEffect(() => { reload(); setShowSubmitForm(false); }, [slug]);

  const storiesByFeature = useMemo(() => {
    const map = new Map<number | 'unassigned', ProjectStory[]>();
    (gig?.stories || []).forEach((story) => {
      const key = story.featureId ?? 'unassigned';
      map.set(key, [...(map.get(key) || []), story]);
    });
    return map;
  }, [gig]);

  const startProject = async () => {
    if (!gig) return;
    setSaving(true);
    setError(null);
    try {
      const response = await contentApi.enrollProject(gig.id);
      if (!response.data.success) {
        setError(response.data.message || 'Unable to start this project.');
        return;
      }
      setMessage('Project started. Work at your own pace, then submit your Git repository for review when you are done.');
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to start this project.');
    } finally {
      setSaving(false);
    }
  };

  const submitWork = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!gig) return;
    setSaving(true);
    setError(null);
    try {
      const response = await contentApi.submitProjectWork(gig.id, {
        gitRepoUrl,
        architectureDocUrl: architectureDocUrl || undefined,
        setupInstructionsUrl: setupInstructionsUrl || undefined,
      });
      if (!response.data.success) {
        setError(response.data.message || 'Unable to submit for review.');
        return;
      }
      setMessage(gig.enrollment?.status === 'Submitted' ? 'Submission updated.' : 'Submitted for review.');
      setShowSubmitForm(false);
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to submit for review.');
    } finally {
      setSaving(false);
    }
  };

  const requestMentor = async () => {
    if (!gig) return;
    setSaving(true);
    setError(null);
    try {
      const response = await contentApi.requestProjectMentorSupport(gig.id, {
        rolePreference: 'Mentor',
      });
      if (!response.data.success) {
        setError(response.data.message || 'Unable to request mentor support.');
        return;
      }
      setMessage(response.data.message || 'Mentor support requested. An admin will follow up.');
      reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to request mentor support.');
    } finally {
      setSaving(false);
    }
  };

  if (error && !gig) {
    return <div className="max-w-3xl mx-auto px-4 py-16 p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</div>;
  }
  if (!gig) return <PageLoading label="Loading project…" />;

  const featureStories = (feature: ProjectFeature) => storiesByFeature.get(feature.id ?? -1) || [];
  const enrollment = gig.enrollment;
  const submitted = enrollment?.status === 'Submitted';
  const hoursLabel = formatApproxHours(gig.estimatedHours);
  const audienceLabel = projectAudienceLabel(gig.teamType);
  const techItems = splitTechStack(gig.techStack);
  const mentorOpen = !!(gig.mentorRequest && /open/i.test(gig.mentorRequest.status));
  const mentorAssigned = !!(gig.mentorRequest && !mentorOpen);
  const togglePanel = (id: string) => {
    setOpenPanel((current) => (current === id ? '' : id));
    setOpenEpicKey(null);
    setOpenFeatureKey(null);
  };
  const focusBelowHero = () => {
    document.getElementById('project-below-hero')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    document.getElementById('project-content')?.focus({ preventScroll: true });
  };
  const chooseSection = (id: SectionId) => {
    setSection(id);
    setOpenEpicKey(null);
    setOpenFeatureKey(null);
    requestAnimationFrame(focusBelowHero);
  };
  const epicsList = (gig.epics && gig.epics.length > 0)
    ? gig.epics
    : [{ title: gig.epicTitle, description: gig.epicDescription }];

  const techStackList = techItems.length > 0 ? (
    <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-700">
      {techItems.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  ) : null;

  const renderOverview = () => (
    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{gig.summary || gig.subtitle || 'No overview provided.'}</p>
  );

  const renderSpec = () => (
    <>
      <Card className="p-5">
        {(gig.specBlocks && gig.specBlocks.length > 0)
          ? <ContentBlocks blocks={gig.specBlocks} />
          : <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{gig.spec || 'No specification yet.'}</p>}
      </Card>
      <AssetList assets={(gig.sectionAssets || []).filter((a) => a.sectionKey === 'spec')} />
    </>
  );

  const renderRequirementsTree = () => (
    <div className="space-y-2">
      {epicsList.map((epic, epicIndex) => {
        const epicKey = String(epic.id ?? `epic-${epicIndex}`);
        const epicFeatures = gig.features.filter((feature) => !epic.id || feature.epicId === epic.id || (!feature.epicId && epic.title === gig.epicTitle));
        return (
          <DetailAccordion
            key={epicKey}
            title={epic.title || 'Untitled epic'}
            open={openEpicKey === epicKey}
            onToggle={() => { setOpenEpicKey((current) => current === epicKey ? null : epicKey); setOpenFeatureKey(null); }}
          >
            {epic.description && <p className="mb-3 text-gray-700 leading-relaxed whitespace-pre-wrap">{epic.description}</p>}
            <div className="space-y-2">
              {epicFeatures.map((feature, featureIndex) => {
                const featureKey = String(feature.id ?? `f-${epicKey}-${featureIndex}`);
                return (
                  <DetailAccordion
                    key={featureKey}
                    title={feature.title || 'Untitled feature'}
                    open={openFeatureKey === featureKey}
                    onToggle={() => setOpenFeatureKey((current) => current === featureKey ? null : featureKey)}
                  >
                    {feature.description && <p className="mb-3 text-sm text-gray-600 whitespace-pre-wrap">{feature.description}</p>}
                    <ul className="space-y-3">
                      {featureStories(feature).map((story) => (
                        <li key={story.id || story.title} className="rounded-lg bg-gray-50 p-3">
                          <p className="font-medium text-gray-900">{story.title}</p>
                          {story.description && <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{story.description}</p>}
                          {story.acceptanceCriteria && (
                            <p className="mt-2 text-xs text-gray-500 whitespace-pre-wrap">Acceptance: {story.acceptanceCriteria}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </DetailAccordion>
                );
              })}
            </div>
          </DetailAccordion>
        );
      })}
      {(storiesByFeature.get('unassigned') || []).length > 0 && (
        <ul className="mt-2 space-y-3">
          {storiesByFeature.get('unassigned')!.map((story) => (
            <li key={story.id || story.title} className="rounded-lg bg-gray-50 p-3">
              <p className="font-medium text-gray-900">{story.title}</p>
              {story.description && <p className="mt-1 text-sm text-gray-600">{story.description}</p>}
            </li>
          ))}
        </ul>
      )}
      <AssetList assets={(gig.sectionAssets || []).filter((a) => a.sectionKey === 'requirements')} />
    </div>
  );

  const renderWireframes = () => (
    <>
      {gig.wireframes.length === 0 && <p className="text-gray-500">No wireframes published.</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        {gig.wireframes.map((frame) => (
          <Card key={frame.id || frame.title} className="overflow-hidden">
            {frame.media?.url && (frame.media.contentType || 'image/').startsWith('image/') && (
              <img src={frame.media.url} alt={frame.media.altText || frame.title} className="w-full h-48 object-cover" />
            )}
            {frame.media?.url && !(frame.media.contentType || 'image/').startsWith('image/') && (
              <a href={frame.media.url} className="block p-4 text-sm text-primary-700" target="_blank" rel="noreferrer">{frame.media.fileName || 'Open file'}</a>
            )}
            <div className="p-4">
              <h3 className="font-semibold text-gray-900">{frame.title}</h3>
              {frame.description && <p className="mt-1 text-sm text-gray-600">{frame.description}</p>}
            </div>
          </Card>
        ))}
      </div>
    </>
  );

  const renderArchitecture = () => (
    <>
      <Card className="p-5">
        {(gig.architectureBlocks && gig.architectureBlocks.length > 0)
          ? <ContentBlocks blocks={gig.architectureBlocks} />
          : <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{gig.architectureOverview || 'No architecture notes yet.'}</p>}
      </Card>
      <AssetList assets={(gig.sectionAssets || []).filter((a) => a.sectionKey === 'architecture')} />
    </>
  );

  const renderErrors = () => (
    <>
      {gig.errorMessages.length === 0 && <p className="text-gray-500">No error catalog published.</p>}
      <div className="space-y-3">
        {gig.errorMessages.map((item) => (
          <Card key={item.id || item.code || item.scenario} className="p-4">
            <div className="flex flex-wrap gap-2 items-baseline">
              {item.code && <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-700">{item.code}</span>}
              <p className="font-medium text-gray-900">{item.scenario}</p>
            </div>
            <p className="mt-2 text-sm text-gray-700">{item.message}</p>
          </Card>
        ))}
      </div>
    </>
  );

  const startCard = (!enrollment || !isAuthenticated) ? (
    <Card className="p-6">
      {!isAuthenticated && (
        <div>
          <p className="text-gray-700">Sign in to start this self-paced project and submit your work for review.</p>
          <Link to="/login" className="mt-4 inline-flex"><Button>Sign in to start</Button></Link>
        </div>
      )}
      {isAuthenticated && !enrollment && (
        <div>
          <p className="text-gray-700">Start this project to track your work. Submit your Git repository for review when you are done.</p>
          <Button className="mt-4" isLoading={saving} onClick={startProject}>Start this project</Button>
        </div>
      )}
    </Card>
  ) : null;

  return (
    <article>
      <section className={heroShell('projects')}>
        <HeroBackdrop tone="projects" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[1.35rem] md:py-[1.575rem] lg:py-[1.8rem]">
          <div className="grid lg:grid-cols-2 gap-[1.125rem] lg:gap-[1.8rem] items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">{gig.company}</p>
              <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">{gig.title}</h1>
              {gig.subtitle && <p className="mt-4 text-sm sm:text-base text-primary-100/90 leading-relaxed">{gig.subtitle}</p>}
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm text-primary-100/90">
                <span>{gig.difficulty}</span>
                {hoursLabel && <span>{hoursLabel}</span>}
                <span>{audienceLabel}</span>
              </div>
            </div>
            <div className="relative">
              <div className="relative overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
                <img
                  src={gig.coverImageUrl || COURSE_IMAGES.projectsHero}
                  alt=""
                  className="w-full h-[12.6rem] sm:h-[15.3rem] lg:h-[18rem] object-cover object-center"
                />
                <div className={heroImageFade('projects')} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div id="project-below-hero" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 scroll-mt-24">
        {message && <p className="mb-4 p-3 rounded-md bg-primary-50 text-primary-800 border border-primary-200">{message}</p>}
        {error && gig && <p className="mb-4 p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</p>}

        <div className="space-y-4 mb-8">
          {startCard}

          {enrollment && (
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex flex-wrap items-center gap-3 min-w-0">
                  <p className="font-semibold text-gray-900">{submitted ? 'Submitted for review' : 'Project started'}</p>
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    submitted ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {enrollment.status}
                  </span>
                  <Button type="button" size="sm" variant="secondary" onClick={() => setShowSubmitForm((open) => !open)}>
                    {showSubmitForm ? 'Hide submission' : 'Submit'}
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-3 shrink-0">
                  {mentorOpen && (
                    <span className="text-sm text-primary-800 bg-primary-50 border border-primary-200 rounded-md px-3 py-1.5">
                      Mentor requested
                    </span>
                  )}
                  {mentorAssigned && (
                    <p className="text-sm text-gray-800">
                      Mentor status: <span className="font-semibold">Assigned</span>
                    </p>
                  )}
                  {!gig.mentorRequest && (
                    <Button type="button" size="sm" variant="secondary" isLoading={saving} onClick={() => void requestMentor()}>
                      Request Mentor Support
                    </Button>
                  )}
                </div>
              </div>
              {submitted && enrollment.submittedAt && (
                <p className="mt-2 text-sm text-gray-500">Last submitted {new Date(enrollment.submittedAt).toLocaleString()}.</p>
              )}
              {showSubmitForm && (
                <form onSubmit={submitWork} className="mt-4 space-y-4">
                  <Input
                    id="gitRepoUrl"
                    label="Git repository"
                    placeholder="https://github.com/you/project"
                    value={gitRepoUrl}
                    onChange={(e) => setGitRepoUrl(e.target.value)}
                    required
                  />
                  <Input
                    id="architectureDocUrl"
                    label="Architecture documentation (Git link)"
                    placeholder="https://github.com/you/project/blob/main/docs/architecture.md"
                    value={architectureDocUrl}
                    onChange={(e) => setArchitectureDocUrl(e.target.value)}
                  />
                  <Input
                    id="setupInstructionsUrl"
                    label="Setup instructions (Git link)"
                    placeholder="https://github.com/you/project/blob/main/README.md"
                    value={setupInstructionsUrl}
                    onChange={(e) => setSetupInstructionsUrl(e.target.value)}
                  />
                  <Button type="submit" isLoading={saving}>
                    {submitted ? 'Resubmit for review' : 'Submit for a review'}
                  </Button>
                </form>
              )}
              {!mentorAssigned && (
                <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                  Request a mentor for technical or project delivery guidance. Your assigned mentor may support you as a subject matter expert, technical lead, or Scrum Master—helping clarify requirements, make decisions, resolve blockers, and keep your project moving.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="lg:hidden space-y-3">
          {SECTIONS.map((item) => (
            <DetailAccordion
              key={item.id}
              title={item.label}
              open={openPanel === item.id}
              onToggle={() => togglePanel(item.id)}
            >
              {item.id === 'tech' && (techStackList || <p className="text-gray-500">No tech stack listed.</p>)}
              {item.id === 'overview' && renderOverview()}
              {item.id === 'spec' && renderSpec()}
              {item.id === 'requirements' && renderRequirementsTree()}
              {item.id === 'wireframes' && renderWireframes()}
              {item.id === 'architecture' && renderArchitecture()}
              {item.id === 'errors' && renderErrors()}
            </DetailAccordion>
          ))}
        </div>

        <div className="hidden lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] gap-8 items-start">
          <aside className="lg:sticky lg:top-24 lg:self-start lg:z-20">
            <nav aria-label="Project sections" className="rounded-2xl border border-gray-200 bg-white p-3 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Contents</p>
              {SECTIONS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => chooseSection(item.id)}
                  className={`w-full text-left rounded-lg px-3 py-2 text-sm ${
                    section === item.id ? 'bg-primary-50 text-primary-800 font-semibold' : 'text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </aside>

          <div id="project-content" tabIndex={-1} className="min-w-0 outline-none">
            <section>
              <h2 className="text-3xl font-semibold text-gray-900 mb-4">
                {SECTIONS.find((item) => item.id === section)?.label}
              </h2>
              {section === 'tech' && (techStackList || <p className="text-gray-500">No tech stack listed.</p>)}
              {section === 'overview' && renderOverview()}
              {section === 'spec' && renderSpec()}
              {section === 'requirements' && renderRequirementsTree()}
              {section === 'wireframes' && renderWireframes()}
              {section === 'architecture' && renderArchitecture()}
              {section === 'errors' && renderErrors()}
            </section>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ProjectDetailPage;
